/**
 * InteriorBody — Weidner Via6 reusable mid-page content section.
 *
 * Mirrors InteriorIntro's rendering (50/50 split: heading + description
 * + CTA on one side, framed image on the other) but is meant to be
 * used *below* the first intro on the same page — e.g. the pet-policy
 * page wants a second InteriorIntro-style block further down. Splitting
 * it into its own slot lets each instance keep its own canonical
 * schema-key namespace (`interiorbody.*`) so the editor's schema
 * sidebar surfaces it as a distinct entry rather than collapsing both
 * sections under the same slot.
 *
 * Like InteriorIntro this is a shared component: each pages.json
 * instance passes a unique `tokenPrefix` so per-instance editor
 * overrides stay scoped to that section.
 *
 * Background = theme primary; copy = text-on-primary.
 */

import { useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// Image slot. Bound to the property photo gallery so a freshly-deployed
// site has something to show before an editor picks a per-page image.
const imageDef = {
  label: 'Body Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Property feature image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const canonicalCtaDef = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Floor Plans',
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

function buildRuntimeCtaDef(ctaUrl: string, ctaLabel: string): SlotGroupDefinition {
  return {
    ...canonicalCtaDef,
    fields: {
      ...canonicalCtaDef.fields,
      url: {
        ...canonicalCtaDef.fields.url,
        defaultValue: ctaUrl,
      },
      label: {
        ...canonicalCtaDef.fields.label,
        defaultValue: ctaLabel,
      },
    },
  };
}

interface InteriorBodyProps {
  /**
   * Per-instance content-schema prefix. Each pages.json instance should
   * pass a unique value (e.g. "pet-policy-intro-secondary") so editor
   * overrides stay scoped to that page section.
   */
  tokenPrefix?: string;
  /**
   * Property field used as the runtime default for the description
   * richtext (e.g. "property_pets_blurb"). Mirrors the rise theme's
   * PageBlurb pattern — each instance passes its own per-page field so
   * inherited copy renders without an editor override. Falls back to
   * the schema literal when omitted.
   */
  blurbField?: string;
  /**
   * Static heading default (e.g. "Pet-friendly perks"). Lets each
   * instance set a sensible title without requiring an editor override.
   */
  pageTitle?: string;
  /**
   * CTA button text. Defaults to "View Floor Plans" — the standard
   * cross-page nudge.
   */
  ctaLabel?: string;
  /** CTA target URL. Pairs with `ctaLabel`. */
  ctaUrl?: string;
  /**
   * Per-instance image URL. When set, this becomes the runtime
   * `defaultValue` for the image slot — overriding the photo_gallery
   * binding so each instance can ship with a topic-relevant photo
   * without an editor override.
   */
  imageUrl?: string;
  /** Alt text paired with `imageUrl`. */
  imageAlt?: string;
  /**
   * Which column the framed image sits in. Defaults to `'right'`.
   * Set to `'left'` to flip the grid so a page with both
   * InteriorIntro and InteriorBody can alternate sides as the eye
   * moves down the page.
   */
  imagePosition?: 'left' | 'right';
  /**
   * Text alignment for the heading / description / CTA column.
   * Defaults to `'left'` (the canonical layout). Set to `'center'`
   * for a more brochure-style centered treatment.
   */
  contentAlign?: 'left' | 'center';
  /**
   * Literal default for the description richtext. Used when the page
   * doesn't have a `blurbField` to pull from but still wants prefilled
   * copy under the heading.
   */
  description?: string;
  /**
   * Whether to render the framed white border around the image.
   * Defaults to `true`. Set to `false` for a flush, borderless
   * treatment.
   */
  framed?: boolean;
}

export function InteriorBody({
  tokenPrefix,
  blurbField,
  pageTitle,
  ctaLabel = 'View Floor Plans',
  ctaUrl = '/floor-plans',
  imageUrl,
  imageAlt,
  imagePosition = 'right',
  contentAlign = 'left',
  description,
  framed = true,
}: InteriorBodyProps) {
  // Fallback matches the canonical schema-key first segment so the
  // component is usable standalone (previews, tests).
  const prefix = tokenPrefix || 'interiorbody';
  const headingPath = `${prefix}.heading`;
  const descriptionPath = `${prefix}.description`;
  const ctaPath = `${prefix}.cta`;
  const imagePath = `${prefix}.image`;

  const cta = useSlotGroup(ctaPath, buildRuntimeCtaDef(ctaUrl, ctaLabel));

  // When the page passes a literal `imageUrl`, swap the photo_gallery
  // binding for an explicit defaultValue so the page renders the
  // topic-relevant photo without requiring an editor override.
  const perInstanceImageDef = imageUrl
    ? {
        label: 'Body Image',
        fields: {
          image: {
            label: 'Image',
            type: 'image',
            defaultValue: imageUrl,
          },
          alt: {
            label: 'Alt Text',
            type: 'text',
            defaultValue: imageAlt || 'Property feature image',
          },
        },
      }
    : imageDef;

  const image = useSlotGroup(imagePath, perInstanceImageDef);

  // Lazy-load the "Satisfy" cursive Google Font used by the
  // .vibe-cursive-accent span when an editor (or the per-instance
  // heading default) wraps a word in this accent treatment. De-duped
  // against the other components that use the same accent by the
  // data-vibe-font marker so we never inject the stylesheet link more
  // than once.
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="satisfy"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Satisfy&display=swap';
    link.setAttribute('data-vibe-font', 'satisfy');
    document.head.appendChild(link);
  }, []);

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-primary)]">
      {/* Cursive accent rule for any <span class="vibe-cursive-accent">
          word in the heading default or editor override. text-transform:
          none escapes the heading's uppercase utility so the script
          glyphs render in their natural mixed case. Mirrors the same
          treatment used by InteriorIntro and other branded headings. */}
      <style>{`
        .vibe-cursive-accent {
          font-family: 'Satisfy', cursive;
          color: var(--vibe-color-secondary);
          font-weight: 400;
          text-transform: none;
          letter-spacing: 0;
        }
      `}</style>
      <div className="max-w-7xl xl:max-w-[88rem] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Content column — heading, description, CTA.
              `lg:order-*` lets us flip the column order based on the
              `imagePosition` prop without duplicating the JSX. The
              `contentAlign` prop drives the text alignment + CTA
              wrapper alignment so the same component supports both the
              canonical left-aligned and a centered brochure-style
              treatment on the same page. */}
          <div
            className={`text-[var(--vibe-color-text-on-primary)] ${
              imagePosition === 'left' ? 'lg:order-2' : 'lg:order-1'
            } ${contentAlign === 'center' ? 'text-center' : 'text-left'}`}
          >
            <RichTextContent
              path={headingPath}
              defaultValue={pageTitle || 'Discover Your New Home'}
              as="h2"
              className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal leading-tight uppercase tracking-wide [&_strong]:font-normal [&_b]:font-normal"
            />

            <RichTextContent
              path={descriptionPath}
              defaultValue={description}
              defaultField={blurbField}
              as="div"
              className="mt-6 font-body text-base sm:text-lg leading-relaxed text-[var(--vibe-color-text-on-primary)] [&_a]:text-[var(--vibe-color-text-on-primary)] [&_a]:underline [&_strong]:font-semibold [&_p]:mt-4 first:[&_p]:mt-0"
            />

            <div className={`mt-8 ${contentAlign === 'center' ? 'flex justify-center' : ''}`}>
              <EditableSlotGroup groupPath={ctaPath} definition={buildRuntimeCtaDef(ctaUrl, ctaLabel)}>
                <a
                  href={cta.url || ctaUrl}
                  target={cta._target || '_self'}
                  className="inline-block text-center px-8 py-3 text-sm font-medium tracking-[0.2em] uppercase rounded-full bg-[var(--vibe-color-secondary)] text-[var(--vibe-color-text-on-primary)] border border-[var(--vibe-color-secondary)] hover:bg-transparent hover:border-[var(--vibe-color-text-on-primary)] transition-all"
                >
                  {cta.label || ctaLabel}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Image column — framed image (2px white border, 20px inner gap).
              Paired `lg:order-*` with the content column so `imagePosition`
              flips which side the image sits on. */}
          <div className={imagePosition === 'left' ? 'lg:order-1' : 'lg:order-2'}>
            <EditableSlotGroup groupPath={imagePath} definition={perInstanceImageDef}>
              {/* `framed` toggles the 2px white border + 20px inner gap
                  (the canonical "matted" look). When false, the image
                  sits flush with no border. */}
              <div className={framed ? 'border-2 border-white p-[20px] w-full' : 'w-full'}>
                {image.image ? (
                  <VibesitesImage
                    url={image.image}
                    alt={image.alt || ''}
                    className="block w-full h-auto object-cover aspect-[16/9]"
                  />
                ) : (
                  // Brand-tone fallback when no photo is set yet, so the
                  // white frame still reads instead of showing a broken
                  // image.
                  <div className="w-full aspect-[16/9] bg-[var(--vibe-color-text)]/20" />
                )}
              </div>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      {/*
        Schema-lint anchors — hidden, never interactive. The visible
        wrappers above use dynamic per-instance paths (built from the
        `tokenPrefix` prop). The schema linter only sees literal
        `path=` / `groupPath=` strings, so we duplicate the canonical
        schema keys here so each `tokens` / `slotGroups` entry has a
        matching wrapper in source. See
        reference/shared-component-pattern.md for the rationale.
      */}
      <RichTextContent
        path="interiorbody.heading"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="interiorbody.description"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="interiorbody.cta"
        definition={canonicalCtaDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="interiorbody.image"
        definition={imageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InteriorBody',
  displayName: 'Interior Page Body',
  tokens: {
    'interiorbody.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: 'Discover Your New Home',
    },
    'interiorbody.description': {
      label: 'Description',
      type: 'richtext',
      defaultValue:
        '<p>Welcome to <span data-type="field-token" data-field="property_name"></span>. Explore everything our community has to offer and find the perfect place to call home.</p>',
    },
  },
  slotGroups: {
    'interiorbody.cta': canonicalCtaDef,
    'interiorbody.image': imageDef,
  },
};

(InteriorBody as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default InteriorBody;
